import { useTranslation } from "react-i18next";
import { DetailSection, EntityHeader, EntityChip, type EntityLookup } from "./detailParts";
import type { OntologyRelation } from "./relationTree";
import type { OntologyEntity } from "./types";
import { entityDisplayName } from "./types";

interface OntologyRelationDetailsProps {
  entity: OntologyEntity | null;
  incomingRelations: OntologyRelation[];
  outgoingRelations: OntologyRelation[];
  lookup: EntityLookup;
}

type RelationDirection = "incoming" | "outgoing";

function RelationList({
  values,
  direction,
  lookup,
  emptyText,
}: {
  values: OntologyRelation[];
  direction: RelationDirection;
  lookup: EntityLookup;
  emptyText: string;
}) {
  const { t } = useTranslation();
  if (values.length === 0) {
    return <p className="app-text-muted text-sm">{emptyText}</p>;
  }
  const directionLabel = direction === "outgoing" ? t("ontologyViewer.relationTarget") : t("ontologyViewer.relationSource");

  return (
    <div className="space-y-2">
      {values.map((relation, index) => (
        <div
          key={`${relation.sourceIri}-${relation.propertyIri}-${relation.targetIri}-${String(index)}`}
          className="flex flex-wrap items-center gap-2 text-sm"
        >
          <EntityChip iri={relation.propertyIri} lookup={lookup} />
          <span className="app-text-muted text-xs">{directionLabel}</span>
          <EntityChip iri={direction === "outgoing" ? relation.targetIri : relation.sourceIri} lookup={lookup} />
        </div>
      ))}
    </div>
  );
}

export default function OntologyRelationDetails({ entity, incomingRelations, outgoingRelations, lookup }: OntologyRelationDetailsProps) {
  const { t } = useTranslation();
  if (!entity) {
    return (
      <div className="flex min-h-[360px] items-center justify-center">
        <p className="app-text-muted text-sm">{t("ontologyViewer.noEntitySelected")}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <EntityHeader name={entityDisplayName(entity)} iri={entity.iri} />
      <DetailSection title={t("ontologyViewer.outgoingRelations")}>
        <RelationList
          values={outgoingRelations}
          direction="outgoing"
          lookup={lookup}
          emptyText={t("ontologyViewer.noOutgoingRelations")}
        />
      </DetailSection>
      <DetailSection title={t("ontologyViewer.incomingRelations")}>
        <RelationList
          values={incomingRelations}
          direction="incoming"
          lookup={lookup}
          emptyText={t("ontologyViewer.noIncomingRelations")}
        />
      </DetailSection>
    </div>
  );
}
